import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";
import { AddStarMutation } from "components/engagement/star/graphql/AddStarMutation";
import { RemoveStarMutation } from "components/engagement/star/graphql/RemoveStarMutation";
import type {
  IAddStarMutation,
  IAddStarMutationVariables,
} from "components/engagement/star/graphql/__generated__/AddStarMutation.generated";
import type {
  IRemoveStarMutation,
  IRemoveStarMutationVariables,
} from "components/engagement/star/graphql/__generated__/RemoveStarMutation.generated";
import AddFinancialsTemplateMutation from "components/financials/modern/graphql/AddFinancialsTemplateMutation";
import ChangeFinancialsTemplateVisibilityMutation from "components/financials/modern/graphql/ChangeFinancialsTemplateVisibilityMutation";
import PermanentlyDeleteArchivedTemplatesMutation from "components/financials/modern/graphql/PermanentlyDeleteArchivedTemplatesMutation";
import PermanentlyDeleteFinancialsTemplateMutation from "components/financials/modern/graphql/PermanentlyDeleteFinancialsTemplateMutation";
import SetAsDefaultFinancialsTemplateMutation from "components/financials/modern/graphql/SetAsDefaultFinancialsTemplateMutation";
import ToggleArchivedOnFinancialsTemplateMutation from "components/financials/modern/graphql/ToggleArchivedOnFinancialsTemplateMutation";
import type {
  IAddFinancialsTemplateMutation,
  IAddFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/AddFinancialsTemplateMutation.generated";
import type {
  IChangeFinancialsTemplateVisibilityMutation,
  IChangeFinancialsTemplateVisibilityMutationVariables,
} from "components/financials/modern/graphql/__generated__/ChangeFinancialsTemplateVisibilityMutation.generated";
import type {
  IPermanentlyDeleteArchivedTemplatesMutation,
  IPermanentlyDeleteArchivedTemplatesMutationVariables,
} from "components/financials/modern/graphql/__generated__/PermanentlyDeleteArchivedTemplatesMutation.generated";
import type {
  IPermanentlyDeleteFinancialsTemplateMutation,
  IPermanentlyDeleteFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/PermanentlyDeleteFinancialsTemplateMutation.generated";
import type {
  ISetAsDefaultFinancialsTemplateMutation,
  ISetAsDefaultFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/SetAsDefaultFinancialsTemplateMutation.generated";
import type {
  IToggleArchivedOnFinancialsTemplateMutation,
  IToggleArchivedOnFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/ToggleArchivedOnFinancialsTemplateMutation.generated";
import handleMessageGraphQLErrors from "components/utils/handleMessageGraphQLErrors";
import type {
  IEditFinancialsTemplateMutation,
  IEditFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/EditFinancialsTemplateMutation.generated";
import EditFinancialsTemplateMutation from "components/financials/modern/graphql/EditFinancialsTemplateMutation";

export const useTemplateActions = () => {
  const [toggleArchivedOnFinancialsTemplate, { loading: archivedOnFinancialsTemplateLoading }] = useMutation<
    IToggleArchivedOnFinancialsTemplateMutation,
    IToggleArchivedOnFinancialsTemplateMutationVariables
  >(ToggleArchivedOnFinancialsTemplateMutation);

  const [permanentlyDeleteArchivedTemplates, { loading: isPermanentlyDeleteArchivedTemplatesLoading }] = useMutation<
    IPermanentlyDeleteArchivedTemplatesMutation,
    IPermanentlyDeleteArchivedTemplatesMutationVariables
  >(PermanentlyDeleteArchivedTemplatesMutation);

  const [addFinancialsTemplate, { loading: addFinancialsTemplateLoading }] = useMutation<
    IAddFinancialsTemplateMutation,
    IAddFinancialsTemplateMutationVariables
  >(AddFinancialsTemplateMutation, {
    onError: (apolloError) => {
      const message = handleMessageGraphQLErrors(apolloError);

      if (!message) {
        return;
      }

      if (/You've reached the limit of 40 templates/i.test(message)) {
        toast.error("Você atingiu o limite de 40 templates.");
      } else {
        toast.error("Ocorreu um erro ao copiar o template.");
      }
    },
  });

  const [editFinancialsTemplate, { loading: editFinancialsTemplateLoading }] = useMutation<
    IEditFinancialsTemplateMutation,
    IEditFinancialsTemplateMutationVariables
  >(EditFinancialsTemplateMutation);

  const [changeFinancialsTemplateVisibility, { loading: changeVisibilityLoading }] = useMutation<
    IChangeFinancialsTemplateVisibilityMutation,
    IChangeFinancialsTemplateVisibilityMutationVariables
  >(ChangeFinancialsTemplateVisibilityMutation);

  const [permanentlyDeleteFinancialsTemplate, { loading: permanentlyDeleteLoading }] = useMutation<
    IPermanentlyDeleteFinancialsTemplateMutation,
    IPermanentlyDeleteFinancialsTemplateMutationVariables
  >(PermanentlyDeleteFinancialsTemplateMutation);

  const [setAsDefaultFinancialsTemplate, { loading: loadingSetAsDefaultFinancialsTemplate }] = useMutation<
    ISetAsDefaultFinancialsTemplateMutation,
    ISetAsDefaultFinancialsTemplateMutationVariables
  >(SetAsDefaultFinancialsTemplateMutation);

  const [addStar, { loading: isAddingStar }] = useMutation<IAddStarMutation, IAddStarMutationVariables>(
    AddStarMutation,
  );
  const [removeStar, { loading: isRemovingStar }] = useMutation<IRemoveStarMutation, IRemoveStarMutationVariables>(
    RemoveStarMutation,
  );

  return {
    toggleArchivedOnFinancialsTemplate,
    addFinancialsTemplate,
    editFinancialsTemplate,
    changeFinancialsTemplateVisibility,
    permanentlyDeleteFinancialsTemplate,
    permanentlyDeleteArchivedTemplates,
    setAsDefaultFinancialsTemplate,
    addStar,
    removeStar,
    loading: {
      permanentlyDeleteArchivedTemplates: isPermanentlyDeleteArchivedTemplatesLoading,
      archivedOnFinancialsTemplateLoading,
      addFinancialsTemplateLoading,
      editFinancialsTemplateLoading,
      changeVisibilityLoading,
      permanentlyDeleteLoading,
      loadingSetAsDefaultFinancialsTemplate,
      isAddingStar,
      isRemovingStar,
    },
  };
};
